import React, {useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

//components;
import {CustomTooltip, EnhancedTableHead, ImageUploadModal, PaginationTable} from "../../../widgets";
import {makeStyles} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import EditIcon from "@material-ui/icons/Edit";

//helpers:
//crud actions:
import {updateFeature, updateImageFeature, updateTranslation} from "../../../redux_slices/settingsFeaturesSlice";
import TableFooter from "@material-ui/core/TableFooter";
import {Link} from "react-router-dom";
import TranslationModal from "./TranslationModal";
import {loadUserParameter} from "../../../redux_slices/contextSlice";

//styles:
const topicsListStyles = makeStyles(theme => ({
    root: {
        width: "100%",
    },
    table: {
        minWidth: 500,
    },
}));

const TopicsTable = () => {
    const classes = topicsListStyles();
    const {t} = useTranslation();

    //refs:
    const tableRef = useRef(null);

    //redux:
    //actions:
    const dispatch = useDispatch();
    //store:
    const settingsFeatures = useSelector(state => state.settingsFeatures.features);
    const language = useSelector(state => loadUserParameter(state, "language"));

    //local state:
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("title");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [topicId, setTopicId] = useState("");
    const [showEditNameInput, setShowEditNameInput] = useState("");

    // upload image modal:
    const [openUploadImageModal, setOpenUploadImageModal] = useState({
        open: false,
        type: "",
    });
    const [placeholderUploadImg, setPlaceholderUploadImg] = useState("");

    const headRows = [
        {
            id: "thumbnail_url",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.PICUTRE"),
        },
        {
            id: "name",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.NAME"),
        },
    ];

    const onWheel = e => {
        e.preventDefault();
        if (window.innerWidth < 1750) {
            const container = tableRef.current;
            const containerScrollPosition = tableRef.current.scrollLeft;

            container.scrollTo({
                top: 0,
                left: containerScrollPosition + e.deltaY,
            });
        }
    };

    const desc = (a, b, orderBy) => {
        const aLowerCase = (a[orderBy] || "").toString().toLowerCase();
        const bLowerCase = (b[orderBy] || "").toString().toLowerCase();

        if (bLowerCase < aLowerCase) {
            return -1;
        }
        if (bLowerCase > aLowerCase) {
            return 1;
        }
        return 0;
    };

    const stableSort = (array, cmp) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = cmp(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        const stableSorted = stabilizedThis.map(el => el[0]);
        return stableSorted;
    };

    const getSorting = (order, orderBy) => {
        return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
    };

    const handleRequestSort = (event, property) => {
        const isDesc = orderBy === property && order === "desc";
        setOrder(isDesc ? "asc" : "desc");
        setOrderBy(property);
    };

    const onChangeTranslation = async (topicId, values) => {
        await dispatch(updateTranslation({values, topicId}));
    };

    const onTopicChange = async (values, topicId) => {
        await dispatch(updateFeature({values, topicId}));
    };

    const onImageUpload = ({id, file, onConfirmUploaded, onError}) => {
        if (file) {
            const onSuccess = () => {
                onConfirmUploaded();
            };

            let formData = new FormData();
            formData.append("icon", file);
            dispatch(updateImageFeature({id, formData})).then(
                () => onSuccess(),
                e => {
                    onError();
                }
            );
        }
    };

    return (
        <div className={classes.root}>
            <div className={`custom-scroll-area horizontal-scroll`} onWheel={onWheel} ref={tableRef}>
                {settingsFeatures?.length ? (
                    <Table className={classes.table}>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={(settingsFeatures && settingsFeatures.length) || 0}
                            headRows={headRows}
                        />
                        <TableBody>
                            {stableSort(settingsFeatures, getSorting(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((topic, i) => (
                                    <TableRow key={i}>
                                        {/* Column1: thumbnail pic: */}
                                        <TableCell align="left" style={{width: "120px"}}>
                                            <div className="d-flex position-relative w-60px h-60px">
                                                <span
                                                    className="bg_image_cover rounded h-100 w-100 rounded-circle"
                                                    style={{
                                                        backgroundImage: `url(${topic.thumbnail_url ||
                                                            `/media/dummy/dummy-img.jpg`})`,
                                                    }}
                                                />
                                                <CustomTooltip
                                                    config={{
                                                        text: t("TOOLTIP.EDIT.IMAGE"),
                                                        placement: "top",
                                                    }}
                                                >
                                                    <button
                                                        onClick={() => {
                                                            setPlaceholderUploadImg(topic.thumbnail_url);
                                                            setOpenUploadImageModal({
                                                                open: true,
                                                                type: "thumbnail_url",
                                                            });
                                                            setTopicId(topic.feature_typified_id);
                                                        }}
                                                        className={`btn btn-icon btn-light border border-gray position-absolute right-0 bottom-0 edit_button ${showEditNameInput &&
                                                            "d-none"}`}
                                                    >
                                                        <EditIcon />
                                                    </button>
                                                </CustomTooltip>
                                            </div>
                                        </TableCell>

                                        {/* Column2: name */}
                                        <TableCell align="left" component="th" scope="row">
                                            <div className="d-flex align-items-center">
                                                <div className="w-100">
                                                    {showEditNameInput !== topic.feature_typified_id && (
                                                        <div
                                                            className={`${
                                                                showEditNameInput === topic.feature_typified_id
                                                                    ? "d-none"
                                                                    : "d-flex"
                                                            } text-dark-75 font-size-base mb-0 py-2 px-4`}
                                                        >
                                                            {topic.translations.find(
                                                                translation =>
                                                                    translation.language.code === (language || "en-US")
                                                            )?.value || topic.name}
                                                            <CustomTooltip
                                                                config={{
                                                                    text: t("TOOLTIP.EDIT_NAME"),
                                                                    placement: "top",
                                                                }}
                                                            >
                                                                {topic.translations.length ? (
                                                                    <TranslationModal
                                                                        onChange={values =>
                                                                            onChangeTranslation(
                                                                                topic.feature_typified_id,
                                                                                values
                                                                            )
                                                                        }
                                                                        translations={topic.translations}
                                                                    />
                                                                ) : (
                                                                    <button
                                                                        onClick={() => {
                                                                            setShowEditNameInput(
                                                                                topic.feature_typified_id
                                                                            );
                                                                        }}
                                                                        className={`btn btn-icon btn-ligh ml-2 edit_button ${showEditNameInput &&
                                                                            "d-none"}`}
                                                                    >
                                                                        <EditIcon />
                                                                    </button>
                                                                )}
                                                            </CustomTooltip>
                                                        </div>
                                                    )}

                                                    {showEditNameInput === topic.feature_typified_id && (
                                                        <input
                                                            type="text"
                                                            autoFocus
                                                            className={`form-control form-control-solid h-auto py-2 px-4 text-dark-75 font-size-base w-100`}
                                                            defaultValue={topic.name}
                                                            onKeyDown={e => {
                                                                const value = e.target.value.replace(/\s\s+/g, " ");
                                                                if (e.key === "Enter") {
                                                                    const trimmed = value.trim();
                                                                    if (trimmed) {
                                                                        trimmed !== topic?.name &&
                                                                            onTopicChange(
                                                                                {name: trimmed},
                                                                                topic.feature_typified_id
                                                                            );
                                                                    }
                                                                    e.target.value = trimmed;
                                                                    setShowEditNameInput("");
                                                                } else e.target.value = value;
                                                            }}
                                                            onBlur={e => {
                                                                const name = e.target.value
                                                                    .replace(/\s\s+/g, " ")
                                                                    .trim();
                                                                if (name) {
                                                                    name !== topic?.name &&
                                                                        onTopicChange(
                                                                            {name},
                                                                            topic.feature_typified_id
                                                                        );
                                                                }
                                                                e.target.value = name;
                                                                setShowEditNameInput("");
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}

                            <TableRow style={{height: 48}}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        </TableBody>

                        {/* pagination: */}
                        <TableFooter>
                            <TableRow>
                                <PaginationTable
                                    setPage={setPage}
                                    setRowsPerPage={setRowsPerPage}
                                    rows={settingsFeatures}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    rowsPerPageOptions={[5, 10, 25]}
                                    variant={"filled"}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                ) : (
                    <div className="alert alert-light text-dark w-fit-content">
                        {t("ADMINPANEL.ICONS.EMPTY_MESSAGE")}
                        <Link to={"/adminpanel/upload"}>{t("ADMINPANEL.UPLOAD.TITLE")}</Link>
                    </div>
                )}
            </div>

            {/* upload image: */}
            <ImageUploadModal
                id={`${topicId}`}
                onUpload={onImageUpload}
                open={openUploadImageModal}
                setOpen={setOpenUploadImageModal}
                placeholder={placeholderUploadImg}
            />
        </div>
    );
};

export default TopicsTable;
